/**
 * Keeps the multi-step group creation flow within the modal that owns it.
 */
import React, { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import ProgressTracker from '@amzn/meridian/progress-tracker';
import ProgressTrackerStep from '@amzn/meridian/progress-tracker/progress-tracker-step';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { ShrinkableTextValue } from 'src/components/common/ShrinkableTextValue';
import { REGIONS } from 'src/constants';
import {
  PERMISSION_LIST_PAGE_SIZE,
  PermissionScopeBusinessLines as ALL_BUSINESS_LINES,
  PermissionScopePublishers as ALL_PUBLISHERS,
} from 'src/store/constants';
import type { Country, CreateGroupDto } from 'src/store/generated/leela-api-generated';
import {
  useBusinessUnitAdminControllerCreateGroupMutation,
  useBusinessUnitAdminControllerFindPartnersQuery,
} from 'src/store/leela-api';
import { GroupRoleLabelKeys } from 'src/utils/groupRoles';
import type { GroupRole } from 'src/utils/groupRoles';

interface CreateGroupModalProps {
  businessUnitId: string;
  businessUnitCountries: string[];
  open: boolean;
  onClose: () => void;
}

interface GroupFormData {
  role: GroupRole | '';
  countries: string[];
  publishers: CreateGroupDto['publishers'];
  businessLines: CreateGroupDto['businessLines'];
  partnerId: string;
}

interface RoleOption {
  readonly value: Exclude<GroupRole, 'campaignLead'>;
  readonly labelKey: string;
  readonly type: 'internal' | 'external';
  readonly descriptionKey: string;
}

const ROLES: readonly RoleOption[] = [
  {
    value: 'campaignManager',
    labelKey: GroupRoleLabelKeys.campaignManager,
    type: 'internal',
    descriptionKey: 'create_group_role_campaign_manager_description',
  },
  {
    value: 'analyst',
    labelKey: GroupRoleLabelKeys.analyst,
    type: 'internal',
    descriptionKey: 'create_group_role_analyst_description',
  },
  {
    value: 'agencyOperator',
    labelKey: GroupRoleLabelKeys.agencyOperator,
    type: 'external',
    descriptionKey: 'create_group_role_agency_operator_description',
  },
  {
    value: 'agencyViewer',
    labelKey: GroupRoleLabelKeys.agencyViewer,
    type: 'external',
    descriptionKey: 'create_group_role_agency_viewer_description',
  },
];
const SELECT_ALL = '__SELECT_ALL__';
const DESELECT_ALL = '__DESELECT_ALL__';

type GroupModalStep = 'role' | 'scope' | 'review';
const STEPS: GroupModalStep[] = ['role', 'scope', 'review'];
const STEP_LABEL_KEYS: Record<GroupModalStep, string> = {
  role: 'create_group_step_role',
  scope: 'create_group_step_scope',
  review: 'create_group_step_review',
};

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ businessUnitId, businessUnitCountries, open, onClose }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [createGroup, { isLoading }] = useBusinessUnitAdminControllerCreateGroupMutation();
  const { data: partnersData } = useBusinessUnitAdminControllerFindPartnersQuery({
    id: businessUnitId,
    page: 1,
    pageSize: PERMISSION_LIST_PAGE_SIZE,
  });
  const partners = (partnersData?.data ?? []) as Array<{ id: string; name: string }>;
  const [currentStep, setCurrentStep] = useState<GroupModalStep>('role');

  const { control, setValue, getValues, reset } = useForm<GroupFormData>({
    defaultValues: {
      role: '',
      countries: [],
      publishers: [],
      businessLines: [],
      partnerId: '',
    },
  });

  const selectedRole = useWatch({ control, name: 'role' }) as GroupFormData['role'];
  const selectedCountries = useWatch({ control, name: 'countries' }) as string[];
  const selectedPublishers = useWatch({ control, name: 'publishers' }) as CreateGroupDto['publishers'];
  const selectedBusinessLines = useWatch({ control, name: 'businessLines' }) as CreateGroupDto['businessLines'];
  const selectedPartnerId = useWatch({ control, name: 'partnerId' }) as string;

  const isExternalRole = ROLES.find(r => r.value === selectedRole)?.type === 'external';
  const currentStepIndex = STEPS.indexOf(currentStep);

  const handleRoleSelect = (role: RoleOption) => {
    setValue('role', role.value);
    if (role.type === 'internal') setValue('partnerId', '');
  };

  const handleNext = () => {
    const idx = STEPS.indexOf(currentStep);
    if (idx < STEPS.length - 1) setCurrentStep(STEPS[idx + 1]);
  };

  const handleBack = () => {
    const idx = STEPS.indexOf(currentStep);
    if (idx > 0) setCurrentStep(STEPS[idx - 1]);
  };

  const handleSubmit = async () => {
    const data = getValues();
    if (!data.role) return;

    try {
      await createGroup({
        createGroupDto: {
          businessUnitId,
          role: data.role,
          // Empty arrays are distinct unconstrained grants that include values added later.
          countries: data.countries as Country[],
          publishers: data.publishers,
          businessLines: data.businessLines,
          dataVisibilityPolicy: (isExternalRole ? 'AGENCY_RESTRICTED' : 'FULL') as 'FULL' | 'DIRECTIONAL' | 'AGENCY_RESTRICTED',
          ...(data.partnerId && { partnerId: data.partnerId }),
        },
      }).unwrap();
      createToast({ type: AlertType.SUCCESS, message: t('access_management_group_created') });
      reset();
      setCurrentStep('role');
      onClose();
    } catch (error: unknown) {
      const apiMessage = (error as { data?: { message?: string } })?.data?.message;
      createToast({ type: AlertType.ERROR, message: apiMessage ?? t('access_management_group_create_error') });
    }
  };

  const handleClose = () => {
    reset();
    setCurrentStep('role');
    onClose();
  };

  return (
    <Modal
      data-testid="create-group-wizard"
      open={open}
      scrollContainer="modal"
      title={t('access_management_create_group')}
      width={700}
      onClose={handleClose}
    >
      <Column spacing="400">
        <ProgressTracker autoTrackLength direction="row">
          {STEPS.map((step, idx) => (
            <ProgressTrackerStep
              key={step}
              label={t(STEP_LABEL_KEYS[step])}
              type={idx < currentStepIndex ? 'past' : idx === currentStepIndex ? 'present' : 'future'}
            />
          ))}
        </ProgressTracker>

        {currentStep === 'role' && (
          <Column data-testid="wizard-step-role" spacing="300">
            <Text type="h100">{t('create_group_select_role')}</Text>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {ROLES.map(role => (
                <div
                  aria-pressed={selectedRole === role.value}
                  data-testid={`wizard-role-${role.value}`}
                  key={role.value}
                  role="button"
                  style={{
                    border: selectedRole === role.value ? '2px solid #0972d3' : '1px solid #d5dbdb',
                    borderRadius: '6px',
                    padding: '12px',
                    cursor: 'pointer',
                    background: selectedRole === role.value ? '#f0f8ff' : '#fff',
                    textAlign: 'left',
                  }}
                  tabIndex={0}
                  onClick={() => { handleRoleSelect(role); }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleRoleSelect(role);
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Text type="b200">{t(role.labelKey)}</Text>
                    <span style={{
                      fontSize: '11px',
                      padding: '1px 6px',
                      borderRadius: '8px',
                      background: role.type === 'internal' ? '#d5f0d5' : '#ffe9d1',
                      color: role.type === 'internal' ? '#1a5c1a' : '#7a4a00',
                    }}>
                      {t(role.type === 'internal' ? 'create_group_type_internal' : 'create_group_type_external')}
                    </span>
                  </div>
                  <Text color="secondary" type="b100">{t(role.descriptionKey)}</Text>
                </div>
              ))}
            </div>
          </Column>
        )}

        {currentStep === 'scope' && (
          <Column data-testid="wizard-step-scope" spacing="300">
            <Text type="h100">{t('create_group_define_scope')}</Text>
            <Text color="secondary" data-testid="wizard-scope-help" type="b200">
              {t('create_group_scope_help')}
            </Text>

            <Column spacing="100">
              <Text type="b200">{t('create_group_countries_label')}</Text>
              <Select
                data-testid="wizard-scope-countries"
                placeholder={t('create_group_countries_placeholder')}
                value={selectedCountries ?? []}
                onChange={(v: string | string[]) => {
                  const values = Array.isArray(v) ? v : [v];
                  if (values.includes(SELECT_ALL)) {
                    setValue('countries', [...businessUnitCountries]);
                  } else if (values.includes(DESELECT_ALL)) {
                    setValue('countries', []);
                  } else {
                    const regionValues = values.filter(val => val.startsWith('REGION:'));
                    const countryValues = values.filter(val => !val.startsWith('REGION:'));
                    const nextCountries = new Set(countryValues);

                    regionValues.forEach(regionValue => {
                      const regionName = regionValue.replace('REGION:', '');
                      const region = REGIONS.find(candidate => candidate.name === regionName);
                      const regionCountries = (region?.countries ?? [])
                        .filter(country => businessUnitCountries.includes(country as string)) as string[];
                      const isRegionSelected = regionCountries.every(country => countryValues.includes(country));

                      regionCountries.forEach(country => {
                        if (isRegionSelected) {
                          nextCountries.delete(country);
                        } else {
                          nextCountries.add(country);
                        }
                      });
                    });
                    setValue('countries', Array.from(nextCountries));
                  }
                }}
              >
                <SelectOption
                  label={selectedCountries?.length === businessUnitCountries.length ? t('create_group_deselect_all') : t('create_group_select_all')}
                  value={selectedCountries?.length === businessUnitCountries.length ? DESELECT_ALL : SELECT_ALL}
                />
                {REGIONS
                  .filter(region => region.countries.some(c => businessUnitCountries.includes(c as string)))
                  .map(region => {
                    const regionCountries = region.countries.filter(c => businessUnitCountries.includes(c as string));
                    return [
                      <SelectOption key={`region-${region.name}`} label={`── ${region.name} (${regionCountries.length}) ──`} value={`REGION:${region.name}`} />,
                      ...regionCountries.map(c => (
                        <SelectOption key={c} label={`  ${c}`} value={c as string} />
                      )),
                    ];
                  })
                  .flat()}
              </Select>
            </Column>

            <Column spacing="100">
              <Text type="b200">{t('create_group_publishers_label')}</Text>
              <Select
                data-testid="wizard-scope-publishers"
                placeholder={t('create_group_publishers_placeholder')}
                value={selectedPublishers ?? []}
                onChange={(v: string | string[]) => {
                  const values = Array.isArray(v) ? v : [v];
                  if (values.includes(SELECT_ALL)) {
                    setValue('publishers', [...ALL_PUBLISHERS]);
                  } else if (values.includes(DESELECT_ALL)) {
                    setValue('publishers', []);
                  } else {
                    setValue('publishers', values as CreateGroupDto['publishers']);
                  }
                }}
              >
                <SelectOption
                  label={selectedPublishers?.length === ALL_PUBLISHERS.length ? t('create_group_deselect_all') : t('create_group_select_all')}
                  value={selectedPublishers?.length === ALL_PUBLISHERS.length ? DESELECT_ALL : SELECT_ALL}
                />
                {ALL_PUBLISHERS.map(p => (
                  <SelectOption key={p} label={p} value={p} />
                ))}
              </Select>
            </Column>

            <Column spacing="100">
              <Text type="b200">{t('create_group_business_lines_label')}</Text>
              <Select
                data-testid="wizard-scope-business-lines"
                placeholder={t('create_group_business_lines_placeholder')}
                value={selectedBusinessLines ?? []}
                onChange={(v: string | string[]) => {
                  const values = Array.isArray(v) ? v : [v];
                  if (values.includes(SELECT_ALL)) {
                    setValue('businessLines', [...ALL_BUSINESS_LINES]);
                  } else if (values.includes(DESELECT_ALL)) {
                    setValue('businessLines', []);
                  } else {
                    setValue('businessLines', values as CreateGroupDto['businessLines']);
                  }
                }}
              >
                <SelectOption
                  label={selectedBusinessLines?.length === ALL_BUSINESS_LINES.length ? t('create_group_deselect_all') : t('create_group_select_all')}
                  value={selectedBusinessLines?.length === ALL_BUSINESS_LINES.length ? DESELECT_ALL : SELECT_ALL}
                />
                {ALL_BUSINESS_LINES.map(bl => (
                  <SelectOption key={bl} label={bl} value={bl} />
                ))}
              </Select>
            </Column>

            {isExternalRole && (
              <Column spacing="100">
                <Text type="b200">{t('create_group_partner_label')}</Text>
                <Select
                  data-testid="wizard-scope-partner"
                  placeholder={t('create_group_partner_placeholder')}
                  value={selectedPartnerId || ''}
                  onChange={(v: string) => { setValue('partnerId', v); }}
                >
                  {partners.map(p => (
                    <SelectOption key={p.id} label={p.name} value={p.id} />
                  ))}
                </Select>
                {partners.length === 0 && (
                  <Text color="secondary" type="b100">{t('create_group_no_partners')}</Text>
                )}
              </Column>
            )}
          </Column>
        )}

        {currentStep === 'review' && (
          <Column data-testid="wizard-step-review" spacing="300">
            <Text type="h100">{t('create_group_review_title')}</Text>
            <Column spacing="200">
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('create_group_role_label')}</Text>
                <Text type="b200">{t(ROLES.find(role => role.value === selectedRole)?.labelKey ?? 'create_group_step_role')}</Text>
              </Row>
              {isExternalRole && (
                <Row spacing="200">
                  <Text color="secondary" type="b200">{t('create_group_partner_label')}</Text>
                  <ShrinkableTextValue testId="wizard-review-partner-value" type="b200">
                    {partners.find(partner => partner.id === selectedPartnerId)?.name ?? selectedPartnerId}
                  </ShrinkableTextValue>
                </Row>
              )}
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('create_group_countries_review_label')}</Text>
                <ShrinkableTextValue testId="wizard-review-countries-value" type="b200">
                  {selectedCountries?.length ? selectedCountries.join(', ') : t('create_group_all_countries', { countries: businessUnitCountries.join(', ') })}
                </ShrinkableTextValue>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('create_group_publishers_review_label')}</Text>
                <ShrinkableTextValue testId="wizard-review-publishers-value" type="b200">
                  {selectedPublishers?.length ? selectedPublishers.join(', ') : t('create_group_all')}
                </ShrinkableTextValue>
              </Row>
              <Row spacing="200">
                <Text color="secondary" type="b200">{t('create_group_business_lines_review_label')}</Text>
                <ShrinkableTextValue testId="wizard-review-business-lines-value" type="b200">
                  {selectedBusinessLines?.length ? selectedBusinessLines.join(', ') : t('create_group_all')}
                </ShrinkableTextValue>
              </Row>
            </Column>
          </Column>
        )}
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="justify" widths={['fit', 'fit']}>
          <Button
            data-testid="wizard-back-button"
            disabled={currentStepIndex === 0}
            type="secondary"
            onClick={handleBack}
          >
            {t('create_group_back')}
          </Button>
          <Row spacing="300">
            <Button data-testid="wizard-cancel-button" type="secondary" onClick={handleClose}>
              {t('create_group_cancel')}
            </Button>
            {currentStep !== 'review'
              ? (
              <Button
                data-testid="wizard-next-button"
                disabled={
                  (currentStep === 'role' && !selectedRole) ||
                  (currentStep === 'scope' && isExternalRole && !selectedPartnerId)
                }
                type="primary"
                onClick={handleNext}
              >
                {t('create_group_next')}
              </Button>
                )
              : (
              <Button
                data-testid="wizard-create-button"
                disabled={isLoading || (isExternalRole && !selectedPartnerId)}
                type="primary"
                onClick={handleSubmit}
              >
                {t('access_management_create_group')}
              </Button>
                )}
          </Row>
        </Row>
      </ModalFooter>
    </Modal>
  );
};
